import { type HelpArticle, listArticles } from "@/help/registry";
import Fuse from "fuse.js";
import { useId, useMemo } from "react";

export type HelpSearchProps = {
	query: string;
	onQueryChange: (next: string) => void;
	onSelect: (slug: string) => void;
};

/**
 * Inline fuzzy search over the bundled help articles.
 *
 * Why these Fuse options (do not change without revisiting the spec):
 * - `keys: ['title', 'headings', 'body']` — match against the article title
 *   first, then the H2 headings (which we extracted in the registry), then the
 *   raw body text. Title matches naturally outrank body matches via Fuse's
 *   default key weighting.
 * - `threshold: 0.4` — middle-ground fuzziness. Tighter than 0.6 (the default)
 *   so unrelated articles don't surface, looser than 0.2 so a small typo still
 *   matches.
 * - `ignoreLocation: true` — without this, Fuse penalizes matches that appear
 *   late in long article bodies. Help articles are short but the bodies still
 *   dwarf the titles, and we want a deep-body keyword to surface just as well
 *   as a heading keyword.
 * - `minMatchCharLength: 2` — single-character queries return no results to
 *   match the spec's "no results section under 2 chars" rule.
 *
 * The Fuse instance is built once via `useMemo([])` because `listArticles()`
 * reads from a build-time bundle — the article set never changes at runtime in
 * Phase 7A. (Phase 7B will swap registry to async, at which point this becomes
 * a query-keyed `useMemo` with a loading state.)
 */
export function HelpSearch({ query, onQueryChange, onSelect }: HelpSearchProps) {
	const inputId = useId();

	const fuse = useMemo(
		() =>
			new Fuse(listArticles(), {
				keys: ["title", "headings", "body"],
				threshold: 0.4,
				ignoreLocation: true,
				minMatchCharLength: 2,
			}),
		[],
	);

	const trimmed = query.trim();
	const showResults = trimmed.length >= 2;
	const results = showResults ? fuse.search(trimmed).slice(0, 8) : [];

	return (
		<div className="space-y-2">
			<label htmlFor={inputId} className="sr-only">
				Search help
			</label>
			<input
				id={inputId}
				type="search"
				value={query}
				onChange={(e) => onQueryChange(e.target.value)}
				placeholder="Search help…"
				className="w-full rounded-md border border-[#e5e7eb] bg-white px-3 py-2 text-sm text-[#1e1e20] placeholder:text-[#9ca3af] focus:border-[#4f46e5] focus:outline-none focus:ring-1 focus:ring-[#4f46e5]"
			/>
			{showResults ? (
				results.length === 0 ? (
					<p className="px-1 py-2 text-sm text-[#6b7280]">
						No articles match "{trimmed}". Try a different word.
					</p>
				) : (
					<ul className="space-y-1">
						{results.map(({ item }) => (
							<li key={item.slug}>
								<button
									type="button"
									onClick={() => onSelect(item.slug)}
									className="w-full rounded px-2 py-1.5 text-left text-sm hover:bg-[#f3f4f6]"
								>
									<span className="block font-medium text-[#1e1e20]">{item.title}</span>
									<span className="block truncate text-xs text-[#6b7280]">{snippetFor(item)}</span>
								</button>
							</li>
						))}
					</ul>
				)
			) : null}
		</div>
	);
}

function snippetFor(article: HelpArticle): string {
	const firstHeading = article.headings[0];
	if (firstHeading) return firstHeading;
	const collapsed = article.body.replace(/^#.*$/gm, "").replace(/\s+/g, " ").trim();
	return collapsed.length > 80 ? `${collapsed.slice(0, 80)}…` : collapsed;
}
